import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/CarDetails.css';
import { Helmet } from "react-helmet";
import CalendarAvailability from '../components/CalendarAvailability';

// Импорт иконок из src/icons
import calendarIcon from '../icons/car.png';
import carSeatIcon from '../icons/car-seat.png';
import fuelIcon from '../icons/petrol.png';
import transmissionIcon from '../icons/gearbox.png';

// Добавляем опции возврата авто (аналогично подаче)
const returnOptions = [
  { value: '', label: 'Точка возврата не выбрана', cost: 0 },
  { value: 'cityDelivery', label: 'Город', cost: 700 },
  { value: 'airportDelivery', label: 'Аэропорт Храброво', cost: 1200 },
  { value: 'zelenogradskDelivery', label: 'Зеленоградск', cost: 2000 },
  { value: 'svetlogorskDelivery', label: 'Светлогорск', cost: 2500 },
  { value: 'yantarnyDelivery', label: 'Янтарный', cost: 3000 },
];

const CarDetails = () => {
  const [returnLocation, setReturnLocation] = useState('');
  const { carId } = useParams();
  const [car, setCar] = useState(null);
  const navigate = useNavigate();
  const today = new Date();

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('10:00');
  const [endTime, setEndTime] = useState('10:00');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const [days, setDays] = useState(0);

  // Состояние для расчета цены
  const [priceDetails, setPriceDetails] = useState({
    base_price_per_day: 0,
    discounted_price_per_day: 0,
    discount_rate: '1.0',
    discount_description: 'Нет скидки',
    days: 0,
    base_total: 0,
    discounted_total: 0,
    final_total: 0,
    total_coefficient: '1.0'
  });

  // Состояние для дополнительных услуг
  const [additionalServices, setAdditionalServices] = useState({
    none: true,
    cityDelivery: false,
    airportDelivery: false,
    zelenogradskDelivery: false,
    svetlogorskDelivery: false,
    yantarnyDelivery: false,
    childSeat: false,
    carWash: true,
  });

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const response = await axios.get(`https://reservecar.ru/api/cars/${carId}/`);
        setCar(response.data);
      } catch (err) {
        console.error('Ошибка при загрузке автомобиля:', err);
        navigate('/cars');
      }
    };
    if (carId) fetchCar();
  }, [carId, navigate]);

  // Функция для расчета цены
  const calculatePrice = async () => {
    if (!startDate || !endDate || !car) return;
    try {
      const response = await axios.post('https://reservecar.ru/api/calculate-price/', {
        car_id: car.id,
        start_date: startDate,
        end_date: endDate
      });
      setPriceDetails(response.data);
    } catch (err) {
      console.error('Ошибка при расчете цены:', err);
    }
  };

  useEffect(() => {
    if (startDate && endDate && car) {
      calculatePrice();
    }
  }, [startDate, endDate, car]);

  const closeBookingModal = () => {
    setShowModal(false);
    setStartDate('');
    setEndDate('');
    setStartTime('10:00');
    setEndTime('10:00');
    setName('');
    setPhone('');
    setAgreeToTerms(false);
    setReturnLocation('');
    setAdditionalServices({
      none: true,
      cityDelivery: false,
      airportDelivery: false,
      zelenogradskDelivery: false,
      svetlogorskDelivery: false,
      yantarnyDelivery: false,
      childSeat: false,
      carWash: true,
    });
  };

  // Получаем дни из календаря (включительно)
  const handleSelectDates = (startStr, endStr, daysCount) => {
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    };
    setStartDate(formatDate(startStr));
    setEndDate(formatDate(endStr));
    setDays(daysCount);
    setShowModal(true);
  };

  // Стоимость всех доп. услуг (подача + возврат)
  const calculateAdditionalServicesCost = () => {
    let additionalCost = 0;
    if (additionalServices.cityDelivery) additionalCost += 700;
    if (additionalServices.airportDelivery) additionalCost += 1200;
    if (additionalServices.zelenogradskDelivery) additionalCost += 2000;
    if (additionalServices.svetlogorskDelivery) additionalCost += 2500;
    if (additionalServices.yantarnyDelivery) additionalCost += 3000;
    if (additionalServices.carWash) additionalCost += 700;
    // ДОПЛАТА за возврат (returnLocation)
    const retOpt = returnOptions.find(x => x.value === returnLocation);
    if (retOpt && retOpt.cost) additionalCost += retOpt.cost;
    return additionalCost;
  };

  const calculateBookingDetails = () => {
    if (!startDate || !endDate || !car) return { days: 0, rentalCost: 0, additionalCost: 0, totalCost: 0 };
    const additionalCost = calculateAdditionalServicesCost();
    const rentalCost = parseFloat(priceDetails.final_total || 0);
    const finalRentalCost = isNaN(rentalCost) ? 0 : rentalCost;
    const totalCost = finalRentalCost + additionalCost;
    return {
      days: days || parseInt(priceDetails.days || 0, 10),
      rentalCost: finalRentalCost,
      additionalCost: additionalCost,
      totalCost: totalCost
    };
  };

  const getSelectedServices = () => {
    const services = [];
    if (additionalServices.none) services.push('Нет');
    if (additionalServices.cityDelivery) services.push('Подача по городу - 700 руб');
    if (additionalServices.airportDelivery) services.push('Подача в Аэропорт Храброво - 1200 руб');
    if (additionalServices.zelenogradskDelivery) services.push('Подача Зеленоградск - 2000 руб');
    if (additionalServices.svetlogorskDelivery) services.push('Подача Светлогорск - 2500 руб');
    if (additionalServices.yantarnyDelivery) services.push('Подача Янтарный - 3000 руб');
    if (additionalServices.childSeat) services.push('Детское сиденье/Бустер - Бесплатно');
    if (additionalServices.carWash) services.push('Мойка автомобиля - 700 руб');
    if (returnLocation) {
      const ret = returnOptions.find(x => x.value === returnLocation);
      if (ret && ret.cost)
        services.push(`Возврат авто: ${ret.label} - ${ret.cost} руб`);
    }
    return services;
  };

  // Для Telegram: все детали + возврат
  const sendTelegramMessage = async (bookingDetails) => {
    const { days, rentalCost, additionalCost, totalCost } = bookingDetails;
    const selectedServices = getSelectedServices();
    try {
      const response = await axios.post('https://reservecar.ru/api/send-telegram/', {
        days,
        rentalCost: Math.round(rentalCost),
        additionalCost: Math.round(additionalCost),
        totalCost: Math.round(totalCost),
        selectedServices,
        start_date: startDate,
        end_date: endDate,
        start_time: startTime,
        end_time: endTime,
        car: { brand: car.brand, model: car.model },
        name,
        phone,
        discount_description: priceDetails.discount_description,
        returnLocation: returnLocation,
      });
      console.log(response.data.message);
    } catch (error) {
      console.error('Ошибка при отправке в Telegram:', error.response?.data?.error || error.message);
      throw error;
    }
  };

  const handleBook = async (e) => {
    e.preventDefault();
    if (!name || !phone || !startDate || !endDate || !agreeToTerms) {
      alert('Заполните все поля и согласитесь с условиями');
      return;
    }
    const bookingDetails = calculateBookingDetails();
    const bookingData = {
      car_id: car.id,
      start_date: startDate,
      end_date: endDate,
      start_time: startTime,
      end_time: endTime,
      name,
      phone,
      additionalServices: getSelectedServices(),
      discountApplied: priceDetails.discount_description,
      returnLocation,
      total_price: Math.round(bookingDetails.totalCost)
    };
    try {
      await axios.post('https://reservecar.ru/api/bookings/', bookingData);
      await sendTelegramMessage(bookingDetails);
      closeBookingModal();
      navigate('/thank-you');
    } catch (err) {
      console.error('Ошибка бронирования:', err);
      alert('Ошибка при бронировании. Проверьте данные.');
    }
  };

  const handleServiceChange = (service) => {
    if (service === 'carWash') return;
    setAdditionalServices((prev) => {
      if (service === 'none' && !prev.none) {
        return {
          none: true,
          cityDelivery: false,
          airportDelivery: false,
          zelenogradskDelivery: false,
          svetlogorskDelivery: false,
          yantarnyDelivery: false,
          childSeat: false,
          carWash: true,
        };
      }
      const newState = { ...prev, [service]: !prev[service] };
      if (service !== 'none' && newState[service]) {
        newState.none = false;
      }
      return newState;
    });
  };

  if (!car) return <div className="loading">Загрузка...</div>;

  return (
    <div className="car-details-container">
      <div className="car-details">
        <Helmet>
          <title>{`${car.brand} ${car.model} - аренда авто в Калининграде`}</title>
          <meta
            name="description"
            content={`Аренда ${car.brand} ${car.model} в Калининграде. Цена от ${car.price_per_day} руб./сутки. ${car.year} год, ${car.fuel_type}, ${car.transmission}.`}
          />
        </Helmet>
        <div className="car-header">
          <h2>{car.brand} {car.model}</h2>
        </div>
        <img src={car.image} alt={`${car.brand} ${car.model}`} className="car-image"/>
        <p className="car-price">{car.price_per_day} руб./сутки</p>
        <div className="car-info">
          {[
            {icon: calendarIcon, label: 'Год', value: car.year},
            {icon: carSeatIcon, label: 'Мест', value: car.number_of_seats},
            {icon: fuelIcon, label: 'Топливо', value: car.fuel_type},
            {icon: transmissionIcon, label: 'КПП', value: car.transmission}
          ].map((item, index) => (
            <div className="car-info-item" key={index}>
              <img src={item.icon} alt={item.label} className="car-info-icon"/>
              <span className="car-info-text">{item.label}: <strong>{item.value || '-'}</strong></span>
            </div>
          ))}
        </div>
      </div>

      <div className="right-column">
        {/* Блок с дополнительными услугами */}
        <h3 className="additional-services-title">Дополнительные услуги</h3>
        <div className="additional-services">
          <div className="services-list">
            <label className="service-item">
              <input
                type="checkbox"
                checked={additionalServices.none}
                onChange={() => handleServiceChange('none')}
              />
              <span className="service-item-label">Нет</span>
            </label>
            <label className="service-item">
              <input
                type="checkbox"
                checked={additionalServices.cityDelivery}
                onChange={() => handleServiceChange('cityDelivery')}
              />
              <span className="service-item-label">
                Подача по городу
                <span>
                  <span className="service-price">+700</span>
                  <span className="service-price-rub">руб</span>
                </span>
              </span>
            </label>
            <label className="service-item">
              <input
                type="checkbox"
                checked={additionalServices.airportDelivery}
                onChange={() => handleServiceChange('airportDelivery')}
              />
              <span className="service-item-label">
                Подача в Аэропорт Храброво
                <span>
                  <span className="service-price">+1200</span>
                  <span className="service-price-rub">руб</span>
                </span>
              </span>
            </label>
            <label className="service-item">
              <input
                type="checkbox"
                checked={additionalServices.zelenogradskDelivery}
                onChange={() => handleServiceChange('zelenogradskDelivery')}
              />
              <span className="service-item-label">
                Подача в Зеленоградск
                <span>
                  <span className="service-price">+2000</span>
                  <span className="service-price-rub">руб</span>
                </span>
              </span>
            </label>
            <label className="service-item">
              <input
                type="checkbox"
                checked={additionalServices.svetlogorskDelivery}
                onChange={() => handleServiceChange('svetlogorskDelivery')}
              />
              <span className="service-item-label">
                Подача в Светлогорск
                <span>
                  <span className="service-price">+2500</span>
                  <span className="service-price-rub">руб</span>
                </span>
              </span>
            </label>
            <label className="service-item">
              <input
                type="checkbox"
                checked={additionalServices.yantarnyDelivery}
                onChange={() => handleServiceChange('yantarnyDelivery')}
              />
              <span className="service-item-label">
                Подача в Янтарный
                <span>
                  <span className="service-price">+3000</span>
                  <span className="service-price-rub">руб</span>
                </span>
              </span>
            </label>
            <label className="service-item">
              <input
                type="checkbox"
                checked={additionalServices.childSeat}
                onChange={() => handleServiceChange('childSeat')}
              />
              <span className="service-item-label">
                Детское сиденье/Бустер
                <span className="service-free">Бесплатно</span>
              </span>
            </label>
            {/* Мойка автомобиля - всегда отмечена и неактивна */}
            <label className="service-item required-service">
              <input
                type="checkbox"
                checked={additionalServices.carWash}
                disabled={true}
                onChange={() => {}}
              />
              <span className="service-item-label">
                Мойка автомобиля
                <span>
                  <span className="service-price">+700</span>
                  <span className="service-price-rub">руб</span>
                </span>
              </span>
            </label>
          </div>
        </div>
        <div className="calendar-availability">
          <CalendarAvailability
            carId={car.id}
            year={today.getFullYear()}
            month={today.getMonth() + 1}
            onSelectDates={handleSelectDates}
          />
        </div>
      </div>

      {car.additionally && car.additionally.trim() && (
        <div className="additional-info-block">
          <div className="additional-info-content">
            <strong className="additional-info-title">Дополнительно:</strong>
            <p className="additional-info-text">{car.additionally}</p>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeBookingModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Оформление брони</h3>
            <form onSubmit={handleBook}>
              <div>
                <label>Дата начала:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
                <label>Время:</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                />
              </div>
              <div>
                <label>Дата окончания:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
                <label>Время:</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                />
              </div>
              <div>
                <label>Куда вернуть авто:</label>
                <select value={returnLocation} onChange={e => setReturnLocation(e.target.value)}>
                  {returnOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label} {opt.cost ? `(+${opt.cost} руб.)` : ''}</option>
                  ))}
                </select>
              </div>

              <div className="total-cost">
                {priceDetails.discount_rate !== '1.0' && (
                  <div className="discount-info">
                    <p>
                      <strong>{priceDetails.discount_description}</strong>
                    </p>
                    <p>
                      Цена за день: <s>{car.price_per_day} руб.</s> <strong>{Math.round(parseFloat(priceDetails.discounted_price_per_day) || 0)} руб.</strong>
                    </p>
                  </div>
                )}
                <p>
                  Стоимость аренды: <strong>{Math.round(parseFloat(priceDetails.final_total) || 0)} руб.</strong>
                  {calculateBookingDetails().days > 0 && (
                    <span> за {calculateBookingDetails().days} дней</span>
                  )}
                </p>
                <p>
                  Доп услуги: <strong>{Math.round(calculateBookingDetails().additionalCost)} руб.</strong>
                </p>
                <div className="selected-services">
                  <p>Выбранные услуги:</p>
                  <ul>
                    <li key="carwash">Мойка автомобиля - 700 руб</li>
                    {getSelectedServices()
                      .filter(service => !service.includes('Мойка автомобиля') && service !== 'Нет')
                      .map((service, index) => (
                        <li key={index}>{service}</li>
                      ))
                    }
                  </ul>
                </div>
                <p>
                  Итоговая стоимость: <strong>{Math.round(calculateBookingDetails().totalCost)} руб.</strong>
                </p>
              </div>

              <div>
                <label>Ваше имя:</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div>
                <label>Номер телефона:</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
              </div>
              <div className="consent-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                  />
                  Я согласен на обработку персональных данных
                </label>
              </div>
              <div className="modal-buttons">
                <button
                  type="button"
                  onClick={closeBookingModal}
                  className="btn btn-secondary"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!startDate || !endDate || !name || !phone || !agreeToTerms}
                >
                  Подтвердить бронирование
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarDetails;
